import React, { useEffect, useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import { Form, Button, Card, Row, Col, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { FaSearch, FaUndo } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as api from '~/Services/Service';
import {
  selectStylesSm,
  DropdownIndicator,
} from '~/Styles/reactSelectStyles';
import {
  filtrosSchema,
  SEGMENTOS_OPCOES,
  ESTAGIOS_OPCOES,
} from '../validationSchemas';

// Sub-componente que carrega Famílias em cascata por GE/GrupoGE.
// Usa useFormikContext para acessar values e setFieldValue sem prop-drill.
// LICOES Ciclo 9 L3 — flag `ativo` evita race condition entre selects rápidos.
const CascataFamilias = ({ onOpcoesChange, onCarregandoChange }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    let ativo = true;
    const codigoGE = values.codigoGrupoEmpresa;
    const codigoGrupoGE = values.codigoGrupoGe;
    // Sem GE nem GrupoGE → lista vazia (PRD RN-02).
    if (!codigoGE && !codigoGrupoGE) {
      onOpcoesChange([]);
      return () => {
        ativo = false;
      };
    }
    const carregar = async () => {
      onCarregandoChange(true);
      try {
        const familias = await api.listarFamiliasFichaEmpreendimento({
          codigo_grupo_empresa: codigoGE || null,
          codigo_grupo_ge: codigoGrupoGE || null,
        });
        if (!ativo) return;
        onOpcoesChange(
          (familias || []).map((f) => ({
            value: f.codigo_familia,
            label:
              f.nome_da_familia || `Empreendimento ${f.codigo_familia}`,
          }))
        );
      } catch (err) {
        if (!ativo) return;
        const mensagem =
          err && err.message
            ? err.message
            : 'Erro ao carregar empreendimentos.';
        toast.error(mensagem);
      } finally {
        if (ativo) onCarregandoChange(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.codigoGrupoEmpresa, values.codigoGrupoGe]);

  return null;
};

const FormularioFiltros = ({
  opcoesGE,
  opcoesGrupoGE,
  opcoesFamilia,
  carregandoLookupsBase,
  carregandoFamilias,
  carregando,
  onLimpar,
}) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    handleReset,
    submitCount,
  } = useFormikContext();

  const toggleArrayValue = (campo, item) => {
    const atual = values[campo] || [];
    const proximo = atual.includes(item)
      ? atual.filter((v) => v !== item)
      : [...atual, item];
    setFieldValue(campo, proximo);
  };

  const marcarTodos = (campo, valoresPossiveis) => {
    setFieldValue(
      campo,
      valoresPossiveis.map((opt) => opt.value)
    );
  };

  const desmarcarTodos = (campo) => {
    setFieldValue(campo, []);
  };

  const handleSelecionarGE = (opt) => {
    setFieldValue('codigoGrupoEmpresa', opt ? opt.value : null);
    if (opt) setFieldValue('codigoGrupoGe', null); // RN-01 mútua exclusão
    setFieldValue('codigoFamilia', null);
  };

  const handleSelecionarGrupoGE = (opt) => {
    setFieldValue('codigoGrupoGe', opt ? opt.value : null);
    if (opt) setFieldValue('codigoGrupoEmpresa', null); // RN-01 mútua exclusão
    setFieldValue('codigoFamilia', null);
  };

  const handleLimparClick = () => {
    handleReset();
    onLimpar();
  };

  const erroSegmentos =
    (touched.segmentos || submitCount > 0) && errors.segmentos;
  const erroEstagios =
    (touched.estagios || submitCount > 0) && errors.estagios;
  const erroExclusivo =
    submitCount > 0 && typeof errors === 'string' ? errors : null;

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">GE</Form.Label>
        <Select
          styles={selectStylesSm}
          components={{ DropdownIndicator }}
          options={opcoesGE}
          isClearable
          isLoading={carregandoLookupsBase}
          isDisabled={!!values.codigoGrupoGe}
          placeholder="Selecione uma GE…"
          value={
            opcoesGE.find((o) => o.value === values.codigoGrupoEmpresa) ||
            null
          }
          onChange={handleSelecionarGE}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">Grupo de GE</Form.Label>
        <Select
          styles={selectStylesSm}
          components={{ DropdownIndicator }}
          options={opcoesGrupoGE}
          isClearable
          isLoading={carregandoLookupsBase}
          isDisabled={!!values.codigoGrupoEmpresa}
          placeholder="Selecione um Grupo de GE…"
          value={
            opcoesGrupoGE.find((o) => o.value === values.codigoGrupoGe) ||
            null
          }
          onChange={handleSelecionarGrupoGE}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-1">
          Empreendimento (Família)
        </Form.Label>
        <Select
          styles={selectStylesSm}
          components={{ DropdownIndicator }}
          options={opcoesFamilia}
          isClearable
          isLoading={carregandoFamilias}
          isDisabled={!values.codigoGrupoEmpresa && !values.codigoGrupoGe}
          placeholder={
            !values.codigoGrupoEmpresa && !values.codigoGrupoGe
              ? 'Selecione GE ou Grupo primeiro…'
              : 'Todos…'
          }
          value={
            opcoesFamilia.find((o) => o.value === values.codigoFamilia) ||
            null
          }
          onChange={(opt) =>
            setFieldValue('codigoFamilia', opt ? opt.value : null)
          }
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="small mb-0">Segmento do imóvel</Form.Label>
          <div>
            <Button
              variant="link"
              size="sm"
              className="p-0 mr-2"
              onClick={() => marcarTodos('segmentos', SEGMENTOS_OPCOES)}
              type="button"
            >
              Todos
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => desmarcarTodos('segmentos')}
              type="button"
            >
              Nenhum
            </Button>
          </div>
        </div>
        <Row>
          {SEGMENTOS_OPCOES.map((opt) => (
            <Col xs={6} key={opt.value}>
              <Form.Check
                type="checkbox"
                id={`seg-${opt.value}`}
                label={opt.label}
                checked={(values.segmentos || []).includes(opt.value)}
                onChange={() => toggleArrayValue('segmentos', opt.value)}
              />
            </Col>
          ))}
        </Row>
        {erroSegmentos && (
          <Form.Text className="text-danger">{errors.segmentos}</Form.Text>
        )}
      </Form.Group>

      <Form.Group className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="small mb-0">
            Estágio do empreendimento
          </Form.Label>
          <div>
            <Button
              variant="link"
              size="sm"
              className="p-0 mr-2"
              onClick={() => marcarTodos('estagios', ESTAGIOS_OPCOES)}
              type="button"
            >
              Todos
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => desmarcarTodos('estagios')}
              type="button"
            >
              Nenhum
            </Button>
          </div>
        </div>
        <Row>
          {ESTAGIOS_OPCOES.map((opt) => (
            <Col xs={6} key={opt.value}>
              <Form.Check
                type="checkbox"
                id={`est-${opt.value}`}
                label={opt.label}
                checked={(values.estagios || []).includes(opt.value)}
                onChange={() => toggleArrayValue('estagios', opt.value)}
              />
            </Col>
          ))}
        </Row>
        {erroEstagios && (
          <Form.Text className="text-danger">{errors.estagios}</Form.Text>
        )}
      </Form.Group>

      {erroExclusivo && (
        <Form.Text className="text-danger d-block mb-2">
          {erroExclusivo}
        </Form.Text>
      )}

      <Row>
        <Col xs={7}>
          <Button
            type="submit"
            variant="primary"
            block
            disabled={carregando}
          >
            {carregando ? (
              <Spinner animation="border" size="sm" className="mr-1" />
            ) : (
              <FaSearch className="mr-1" />
            )}
            Gerar Relatório
          </Button>
        </Col>
        <Col xs={5}>
          <Button
            type="button"
            variant="outline-secondary"
            block
            onClick={handleLimparClick}
            disabled={carregando}
          >
            <FaUndo className="mr-1" />
            Limpar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

const PainelFiltros = ({
  valoresIniciais,
  onGerar,
  onLimpar,
  carregando,
}) => {
  const [opcoesGE, setOpcoesGE] = useState([]);
  const [opcoesGrupoGE, setOpcoesGrupoGE] = useState([]);
  const [opcoesFamilia, setOpcoesFamilia] = useState([]);
  const [carregandoLookupsBase, setCarregandoLookupsBase] = useState(false);
  const [carregandoFamilias, setCarregandoFamilias] = useState(false);

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      setCarregandoLookupsBase(true);
      try {
        const [ges, gruposGE] = await Promise.all([
          api.listarTodosGruposEmpresa(),
          api.listarGruposGELookup(),
        ]);
        if (!ativo) return;
        setOpcoesGE(
          (ges || []).map((g) => ({
            value: g.codigo || g.codigo_grupo_empresa,
            label:
              g.descricao ||
              g.apelido ||
              g.razao_social ||
              `GE ${g.codigo || g.codigo_grupo_empresa}`,
          }))
        );
        setOpcoesGrupoGE(
          (gruposGE || []).map((g) => ({
            value: g.codigo || g.codigo_grupo_ge,
            label:
              g.descricao || `Grupo ${g.codigo || g.codigo_grupo_ge}`,
          }))
        );
      } catch (err) {
        if (!ativo) return;
        const mensagem =
          err && err.message
            ? err.message
            : 'Erro ao carregar GEs/Grupos de GE.';
        toast.error(mensagem);
      } finally {
        if (ativo) setCarregandoLookupsBase(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  return (
    <Card className="shadow-sm">
      <Card.Header className="font-weight-bold py-2">Filtros</Card.Header>
      <Card.Body>
        <Formik
          enableReinitialize
          initialValues={valoresIniciais}
          validationSchema={filtrosSchema}
          onSubmit={(values) => onGerar(values)}
        >
          <>
            <CascataFamilias
              onOpcoesChange={setOpcoesFamilia}
              onCarregandoChange={setCarregandoFamilias}
            />
            <FormularioFiltros
              opcoesGE={opcoesGE}
              opcoesGrupoGE={opcoesGrupoGE}
              opcoesFamilia={opcoesFamilia}
              carregandoLookupsBase={carregandoLookupsBase}
              carregandoFamilias={carregandoFamilias}
              carregando={carregando}
              onLimpar={onLimpar}
            />
          </>
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default PainelFiltros;
